import React, { useState, useMemo, useRef } from "react";
import { Search, Link, Close } from "@mui/icons-material";
import ResourcesAndReusables from "./ResourcesAndReusables.jsx";
import ContentHeader from "../ContentHeader";
import Modal from "../Modal.jsx";
import Paginator from "../shared/Paginator.jsx";
import MoreBadge from "../shared/MoreBadge.jsx";

import data from "../../../../_data/resources-and-reusables.json";
import { formatProjectDate } from "../../date_utils.js";
import { usePagedList } from "../../hooks/usePagedList.js";

export default function CodeContribution() {
  const members = data?.members;
  const itemsPerPage = 6;

  const selectedMember = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredMembers = useMemo(() => {
    if (!members) return [];
    return members.filter((m) => {
      const matchesSearch =
        !searchTerm ||
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.language || []).some((l) => l.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (m.skill || []).some((s) => s.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesFilters =
        activeFilters.length === 0 ||
        activeFilters.every(
          (f) => m.role === f || (m.language || []).includes(f) || (m.skill || []).includes(f),
        );

      const isActive = m.isActive;

      return matchesSearch && matchesFilters && isActive;
    });
  }, [members, searchTerm, activeFilters]);

  const { currentPage, setCurrentPage, pageItems, pageCount, pagesToDisplay } = usePagedList(
    filteredMembers,
    itemsPerPage,
  );

  if (!members) {
    return (
      <section className="members">
        <div className="members-empty">Members data not found.</div>
      </section>
    );
  }

  const handleClearFilters = () => {
    setActiveFilters([]);
    setSearchTerm("");
  };

  return (
    <>
      <ResourcesAndReusables />

      <ContentHeader
        title="Code Contribution"
        caption={
          "A peek at which projects are assigned to which members, and their past contributions."
        }
      />

      <section className="members">
        <div className="members-header">
          <div className="members-toolbar">
            {activeFilters.length > 0 && (
              <div className="members-filters-active">
                {activeFilters.map((f) => (
                  <div key={f} className="members-filter-chip">
                    {f}
                  </div>
                ))}
                <button className="members-clear-all" onClick={handleClearFilters}>
                  Clear all filters
                </button>
              </div>
            )}

            <div className="members-search-wrapper">
              <div className="members-search-box">
                <Search className="members-search-icon" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="members-grid">
          {pageItems.map((member, i) => {
            const assignedProjects = member.assigned_projects || [];
            const pastProjects = member.past_projects || [];

            return (
              <button
                key={i}
                onClick={() => {
                  selectedMember.current = member;
                  setIsModalOpen(true);
                }}
                className="members-card"
              >
                <div className="members-card-header">
                  <div className="members-avatar">
                    {member.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)}
                  </div>
                  <div className="members-info">
                    <h3 className="members-name">{member.name}</h3>
                    <p className="members-role">{member.role || "Consultant"}</p>
                  </div>
                </div>

                <div className="members-section">
                  <h4 className="members-section-label">Assigned Projects</h4>
                  <div className="members-chips">
                    {assignedProjects.slice(0, 4).map((p, pi) => (
                      <div key={pi} className="members-chip-project">
                        <Link className="members-project-icon" />
                        <span>{p}</span>
                      </div>
                    ))}
                    {assignedProjects.length > 4 && (
                      <MoreBadge
                        items={assignedProjects.slice(4)}
                        count={assignedProjects.length - 4}
                        type="project"
                      />
                    )}
                    {assignedProjects.length < 1 && (
                      <div className="members-chip-project">
                        <span>N/A</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="members-section">
                  <h4 className="members-section-label">Past Projects</h4>
                  <div className="members-chips">
                    {pastProjects.slice(0, 4).map((p, pi) => (
                      <div key={pi} className="members-chip-project">
                        <Link className="members-project-icon" />
                        <span>{p}</span>
                      </div>
                    ))}
                    {pastProjects.length > 4 && (
                      <MoreBadge
                        items={pastProjects.slice(4)}
                        count={pastProjects.length - 4}
                        type="project"
                      />
                    )}
                    {pastProjects.length < 1 && (
                      <div className="members-chip-project">
                        <span>N/A</span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <Paginator
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageCount={pageCount}
          pagesToDisplay={pagesToDisplay}
        />
      </section>

      {isModalOpen && (
        <Modal onClose={() => setIsModalOpen(false)}>
          <ModalContent member={selectedMember.current} />
        </Modal>
      )}
    </>
  );
}

function ModalContent({ member }) {
  const projects = data?.projects;
  const projectMap = new Map();

  const memberProjects = [...(member.assigned_projects || []), ...(member.past_projects || [])];

  for (const project of projects) {
    if (memberProjects.includes(project.title)) {
      const initYear = project.initiatedOn.split("-")[0];
      const yearProjects = projectMap.get(initYear) ?? [];

      yearProjects.push(project);
      projectMap.set(initYear, yearProjects);
    }
  }

  return (
    <>
      <div className="cc-modal">
        <h1>{member.name}</h1>
        <p className="member-sub-text">{member.role}</p>

        <span className="member-project-counters">
          <div className="member-project-counter">
            <p className="member-project-number">{memberProjects.length}</p>
            <p className="member-sub-text">Total Projects</p>
          </div>
          <div className="member-project-counter active-projects">
            <p className="member-project-number">
              {member.assigned_projects ? member.assigned_projects.length : 0}
            </p>
            <p className="member-sub-text">Active Projects</p>
          </div>
          <div className="member-project-counter completed-projects">
            <p className="member-project-number">
              {member.past_projects.length ? member.past_projects.length : 0}
            </p>
            <p className="member-sub-text">Completed Projects</p>
          </div>
        </span>

        <h2>Timeline</h2>

        {Array.from(projectMap.keys())
          .sort((y, next) => y.localeCompare(next))
          .map((y) => {
            return (
              <div className="cc-content" key={y}>
                <div>
                  {projectMap
                    .get(y)
                    .sort((p, next) => p.initiatedOn.localeCompare(next.initiatedOn))
                    .map((p, index) => {
                      return (
                        <div className="cc-entry" key={p.title}>
                          <div className="project-entry">
                            <span>
                              {index == 0 && <h3>{y}</h3>}

                              <div className="entry-circ" />
                            </span>

                            <div className="entry-bar" />
                          </div>

                          <div className="cc-body">
                            <span className="cc-project-header">
                              <h3>{p.title}</h3>
                              <p>{p.completedOn ? "Completed" : "Active"}</p>
                            </span>

                            <span className="cc-details">
                              <p>{member.role}</p>
                              <p>
                                {formatProjectDate(p.initiatedOn)} -{" "}
                                {p.completedOn ? formatProjectDate(p.completedOn) : "Present"}
                              </p>
                            </span>

                            <p>{p.description}</p>

                            {p.gallery && <GalleryShowcase images={p.gallery} />}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
}

function GalleryShowcase({ images, defaultImage = null }) {
  const [enlargedImg, setEnlargedImg] = useState(defaultImage);

  return (
    <>
      <div style={{ display: "block" }}>
        <span className="cc-field">
          <h4>Project Preview</h4>
        </span>
        <div className="cc-gallery">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => {
                setEnlargedImg(img);
              }}
              className="cc-gallery-img expandable"
            >
              <img src={img.src} />
            </button>
          ))}
        </div>
      </div>

      {enlargedImg && (
        <div className="cc-gallery-img enlarged">
          <div className="enlarged-img-controls">
            <button className="enlarged-img-close-btn" onClick={() => setEnlargedImg(null)}>
              <Close fontSize="small" />
            </button>
          </div>
          <img src={enlargedImg.src} />
        </div>
      )}
    </>
  );
}
